import { useQuery } from "@apollo/client/react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { ArrowLeft, DeleteBin, DownloadSave } from "@pmag/icons";
// eslint-disable-next-line no-restricted-imports
import { Button, InlineError, Typography } from "@pmag/ui-components";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { PriceInfoContainer } from "@src/components/priceInfoContainer/PriceInfoContainer";
import { WishlistButton } from "@src/components/wishlistButton/WishlistButton";
import { type GQLWishlistQuery, type GQLWishlistQueryVariables } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useWishlist } from "@src/hooks/useWishlist";
import { useWishlistGoBack } from "@src/main/useWishlistGoBack";
import { WishlistPdf } from "@src/pages/wishlist/components/pdf/WishlistPdf";
import { SmallWishlistItem } from "@src/pages/wishlist/components/smallWishlistItem/SmallWishlistItem";
import { SmallWishlistItemSkeleton } from "@src/pages/wishlist/components/smallWishlistItem/SmallWishlistItemSkeleton";
import { Wishlist } from "@src/pages/wishlist/components/wishlist/Wishlist";
import { resolveWishlistEntry, type WishlistEntryModel } from "@src/pages/wishlist/Wishlist.utils";
import { wishlistQuery } from "@src/pages/wishlist/WishlistPage.gql";
import { type ArticleNumberWithBrand, dispatchRequestArticleInfosForArticlesEvent, Events } from "@src/utils/events";
import { isSPFArticleInfo, isSPFArticleInfosEvent } from "@src/utils/typeGuard.utils";
import * as React from "react";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
    BottomContainer,
    BottomRightContainer,
    CenteredTypography,
    EmptyListContainer,
    ListContainer,
    NavigationButton,
    RemoveButton,
    Root,
    TopContainer,
    TopRightContainer,
} from "./WishlistPage.styles";

export const dataTestId = {
    container: "sparePartsFinder.wishlist.container",
    restartButton: "sparePartsFinder.wishlist.button.restart",
};

export const WishlistPage: React.FunctionComponent = () => {
    const theme = useTheme();
    const intl = useIntl();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { wishlist, removeAllFromWishList } = useWishlist();
    const { hasArticleInfos } = useAttributeConfig();
    const { goBack } = useWishlistGoBack();
    const { priceInfoDisclaimer, quantityInfoDisclaimer } = useAttributeConfig();

    const [wishlistItemWithPriceInfo, setWishlistItemWithPriceInfo] = useState<WishlistEntryModel[]>([]);
    const [artificialLoading, setArtificialLoading] = useState(hasArticleInfos);

    const {
        data,
        loading: queryLoading,
        error,
    } = useQuery<GQLWishlistQuery, GQLWishlistQueryVariables>(wishlistQuery, {
        variables: { spareParts: wishlist.map((item) => resolveWishlistEntry(item)), languageCode: intl.locale === "de" ? "de" : "en" },
        skip: wishlist.length === 0,
    });

    React.useEffect(() => {
        setTimeout(() => {
            setArtificialLoading(false);
        }, 5000);
    }, [hasArticleInfos]);

    useEffect(() => {
        if (hasArticleInfos && data) {
            const articleNumbersWithBrand: ArticleNumberWithBrand[] = data.wishlist.nodes.flatMap((item) =>
                item.article?.articleNumber ? { articleNumber: item.article.articleNumber, brand: item.model.brand?.shortName ?? "" } : [],
            );

            if (articleNumbersWithBrand.length > 0) dispatchRequestArticleInfosForArticlesEvent(articleNumbersWithBrand);
        }
    }, [data, hasArticleInfos]);

    useEffect(() => {
        if (hasArticleInfos && data) {
            const handleArticleInfos = (event: CustomEvent) => {
                if (isSPFArticleInfosEvent(event)) {
                    const itemsWithPrice: WishlistEntryModel[] = data.wishlist.nodes.map((item) => ({
                        ...item,
                        clientArticleInfo: undefined,
                    }));
                    event.detail.infos?.forEach((info: unknown) => {
                        const articleInfo = isSPFArticleInfo(info);
                        if (articleInfo) {
                            itemsWithPrice.map((item) => {
                                if (item.article?.articleNumber === articleInfo.articleNumber) {
                                    item.clientArticleInfo = articleInfo;
                                }
                            });
                        }
                        setWishlistItemWithPriceInfo(itemsWithPrice);
                        setArtificialLoading(false);
                    });
                }
            };

            document.addEventListener(Events.SPFArticleInfos, handleArticleInfos);

            return () => document.removeEventListener(Events.SPFArticleInfos, handleArticleInfos);
        }
    }, [data, hasArticleInfos]);

    if (error && !data) return <InlineError />;

    const wishlistItems =
        wishlist.length === 0 ? [] : wishlistItemWithPriceInfo.length > 0 ? wishlistItemWithPriceInfo : data ? data.wishlist.nodes : [];

    return (
        <Root data-testid={dataTestId.container} pb={8}>
            <TopContainer>
                {!isSmallScreen && (
                    <Typography variant="heading-07">
                        <FormattedMessage id="sparePartsFinder.wishlist.title" defaultMessage="Wishlist" />
                    </Typography>
                )}

                <TopRightContainer>
                    <NavigationButton variant="text" startIcon={<ArrowLeft size="small" />} data-testid={dataTestId.restartButton} onClick={goBack}>
                        <Typography variant="body-short-01">
                            <FormattedMessage id="sparePartsFinder.wishlist.button.back" defaultMessage="Back" />
                        </Typography>
                    </NavigationButton>

                    <WishlistButton />
                </TopRightContainer>
            </TopContainer>

            {isSmallScreen && (
                <Box mb={6}>
                    <Typography variant="heading-07">
                        <FormattedMessage id="sparePartsFinder.wishlist.title" defaultMessage="Wishlist" />
                    </Typography>
                </Box>
            )}

            <ListContainer>
                {!queryLoading && wishlistItems.length === 0 ? (
                    <EmptyListContainer>
                        <CenteredTypography variant="heading-08">
                            <FormattedMessage id="sparePartsFinder.wishlist.emptyTitle" defaultMessage="There are no items on the wish list yet." />
                        </CenteredTypography>

                        <CenteredTypography variant="body-short-02">
                            <FormattedMessage
                                id="sparePartsFinder.wishlist.emptyMessage"
                                defaultMessage="You can add your desired items to the wish list here to request them from your dealer or to print out a list of them."
                            />
                        </CenteredTypography>
                    </EmptyListContainer>
                ) : isSmallScreen ? (
                    queryLoading && wishlistItems.length === 0 ? (
                        Array(wishlist.length)
                            .fill(null)
                            .map((_, index) => (
                                <SmallWishlistItemSkeleton key={`wish-list-item-skeleton-${index}`} showBorderBottom={index < wishlist.length - 1} />
                            ))
                    ) : (
                        wishlistItems.map((item, index) => (
                            <SmallWishlistItem key={item.id} wishlistEntry={item} showBorderBottom={index < wishlistItems.length - 1} />
                        ))
                    )
                ) : (
                    <Wishlist wishlistEntries={wishlistItems} loading={queryLoading && wishlistItems.length === 0} />
                )}
            </ListContainer>

            {wishlist.length > 0 && (
                <>
                    <BottomContainer>
                        <RemoveButton variant="text" icon={<DeleteBin size="small" />} onClick={removeAllFromWishList}>
                            <Typography variant="body-short-01">
                                <FormattedMessage id="sparePartsFinder.wishlist.button.removeAll" defaultMessage="Remove all" />
                            </Typography>
                        </RemoveButton>

                        <BottomRightContainer>
                            <PDFDownloadLink
                                document={
                                    <WishlistPdf
                                        wishlistEntries={wishlistItems}
                                        hasArticleInfos={hasArticleInfos}
                                        priceInfo={priceInfoDisclaimer}
                                        quantityInfo={quantityInfoDisclaimer}
                                        intl={intl}
                                        theme={theme}
                                    />
                                }
                                fileName="spare-parts-finder-wishlist.pdf"
                                style={{ textDecoration: "none" }}
                            >
                                {({ loading: pdfLoading, error }) => (
                                    <Button
                                        disabled={artificialLoading || pdfLoading || !!error}
                                        variant="secondary"
                                        icon={<DownloadSave size="small" />}
                                    >
                                        <FormattedMessage id="sparePartsFinder.wishlist.button.pdfDownload" defaultMessage="PDF Download" />
                                    </Button>
                                )}
                            </PDFDownloadLink>

                            {/*<Button icon={<MailEmailLetter size="small" />}>*/}
                            {/*    <FormattedMessage id="sparePartsFinder.wishlist.button.sendRequest" defaultMessage="Send request" />*/}
                            {/*</Button>*/}
                        </BottomRightContainer>
                    </BottomContainer>

                    <PriceInfoContainer />
                </>
            )}
        </Root>
    );
};
